import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Navigate, useSearchParams } from "react-router";
import type { AuthOptions } from "@klantpunt/shared";
import { Logo } from "../app/Logo";
import { ParentCompany } from "../app/ParentCompany";
import { api } from "../lib/api";
import { useMe } from "../lib/auth";

const ERRORS: Record<string, string> = {
  "geen-toegang": "Dit account heeft geen toegang. Vraag een beheerder om je toe te voegen.",
  mislukt: "Inloggen is mislukt. Probeer het opnieuw.",
  "niet-geverifieerd": "Je Google-account heeft geen geverifieerd e-mailadres.",
  verlopen: "De inlogpoging of inloglink is verlopen. Probeer het opnieuw.",
  "ander-account": "Dit e-mailadres hoort bij een ander Google-account. Neem contact op met een beheerder.",
};

function safe(terug: string | null): string {
  return terug && terug.startsWith("/") && !terug.startsWith("//") ? terug : "/";
}

export function LoginPage() {
  const [params] = useSearchParams();
  const terug = safe(params.get("terug"));
  const error = params.get("fout");
  const { data: me } = useMe();
  const options = useQuery({ queryKey: ["auth-options"], queryFn: () => api<AuthOptions>("/auth/options") });
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (me) return <Navigate to={me.org ? terug : `/organisatie?terug=${encodeURIComponent(terug)}`} replace />;

  async function sendLink(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/auth/link", { method: "POST", body: { email, returnTo: terug } });
      setSent(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="card">
        <Logo />
        <h1 className="dot">Inloggen</h1>
        <p className="muted">Bevindingen vastleggen op de plattegrond</p>

        {error && (
          <div className="alert" role="alert">
            {ERRORS[error] ?? ERRORS.mislukt}
          </div>
        )}

        {options.data?.google === false ? (
          <div className="alert info">Inloggen met Google is nog niet ingesteld.</div>
        ) : (
          <a className="btn primary block" href={`/api/auth/google?terug=${encodeURIComponent(terug)}`}>
            Inloggen met Google
          </a>
        )}

        {options.data?.mail && (
          <>
            <div className="divider">of zonder Google-account</div>
            {sent ? (
              <div className="alert ok" role="status">
                Als dit adres toegang heeft, staat er binnen een minuut een inloglink in je mail.
              </div>
            ) : (
              <form onSubmit={sendLink}>
                <label className="field">
                  <span className="label">E-mailadres</span>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="naam@bedrijf.nl"
                  />
                </label>
                <button type="submit" className="btn block" disabled={busy}>
                  Stuur inloglink
                </button>
              </form>
            )}
          </>
        )}
      </div>
      <ParentCompany />
    </div>
  );
}
